import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion as Motion } from "framer-motion";

import api from "@/utils/axios";
import cls from "./UserMemberPaymentSubmissions.module.scss";

const UserMemberPaymentSubmissions = () => {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submissions, setSubmissions] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const fetchSubmissions = async () => {
      try {
        setLoading(true);
        setError("");

        const res = await api.get(
          `/v1/admin/members/single/${userId}/payment-submissions`,
        );

        if (!isMounted) return;

        setSubmissions(res.data?.submissions || []);
      } catch (err) {
        if (!isMounted) return;
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load payment submissions";
        setError(msg);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    if (userId) {
      fetchSubmissions();
    } else {
      setLoading(false);
      setError("Missing userId in route");
    }

    return () => {
      isMounted = false;
    };
  }, [userId]);

  const stats = useMemo(() => {
    const total = submissions.length;
    const pending = submissions.filter((s) => s.status === "pending").length;
    const approved = submissions.filter((s) => s.status === "approved").length;
    const decline = submissions.filter((s) => s.status === "decline").length;
    return { total, pending, approved, decline };
  }, [submissions]);

  const handleBack = () => {
    navigate(`/admin/members/single/${userId}`);
  };

  const handleViewDetails = (submissionId) => {
    navigate(
      `/admin/members/single/payment-info/single/${userId}/${submissionId}`,
    );
  };

  const formatDate = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className={cls.page}>
      <header className={cls.header}>
        <button type="button" className={cls.backBtn} onClick={handleBack}>
          <span className={cls.backIcon} aria-hidden="true">
            ←
          </span>
          <span>Back to member</span>
        </button>

        <div className={cls.headerText}>
          <h1 className={cls.title}>Payment submissions</h1>
          <p className={cls.subtitle}>
            Review all membership payment submissions made by this member,
            including transfer dates and current status.
          </p>
        </div>
      </header>

      <section className={cls.statsRow}>
        <div className={cls.statCard}>
          <span className={cls.statLabel}>Total submissions</span>
          <span className={cls.statValue}>{stats.total}</span>
        </div>
        <div className={cls.statCard}>
          <span className={cls.statLabel}>Pending</span>
          <span className={cls.statValuePending}>{stats.pending}</span>
        </div>
        <div className={cls.statCard}>
          <span className={cls.statLabel}>Approved</span>
          <span className={cls.statValueApproved}>{stats.approved}</span>
        </div>
        <div className={cls.statCard}>
          <span className={cls.statLabel}>Declined</span>
          <span className={cls.statValueRejected}>{stats.decline}</span>
        </div>
      </section>

      {loading && (
        <div className={cls.stateBox}>
          <div className={cls.loader} />
          <span>Loading payment submissions…</span>
        </div>
      )}

      {!loading && error && (
        <div className={cls.stateBoxError}>
          <span className={cls.stateLabel}>Error</span>
          <p className={cls.stateMessage}>{error}</p>
        </div>
      )}

      {!loading && !error && submissions.length === 0 && (
        <div className={cls.stateBoxEmpty}>
          <h2 className={cls.emptyTitle}>No payment submissions yet</h2>
          <p className={cls.emptyText}>
            This member has not submitted any payment proofs so far. Once they
            upload a payment, it will appear in this list.
          </p>
        </div>
      )}

      {!loading && !error && submissions.length > 0 && (
        <Motion.ul
          className={cls.list}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
        >
          {submissions.map((s, index) => {
            const methodTitle =
              s.paymentOption?.title || "Unknown payment type";
            const status = s.status || "pending";

            return (
              <Motion.li
                key={s.id}
                className={cls.card}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.04 * index, duration: 0.25 }}
              >
                <div className={cls.cardMain}>
                  <div className={cls.cardTopRow}>
                    <div className={cls.cardTitleGroup}>
                      <h2 className={cls.cardTitle}>{methodTitle}</h2>
                      <p className={cls.cardSubtitle}>
                        Submission ID:{" "}
                        <span className={cls.cardId}>{s.id}</span>
                      </p>
                    </div>

                    <span
                      className={`${cls.statusBadge} ${
                        cls[`status-${status}`] || ""
                      }`}
                    >
                      {status === "pending"
                        ? "Pending review"
                        : status === "approved"
                          ? "Approved"
                          : status === "rejected"
                            ? "Rejected"
                            : status}
                    </span>
                  </div>

                  <div className={cls.metaRow}>
                    <div className={cls.metaItem}>
                      <span className={cls.metaLabel}>Transfer date</span>
                      <span className={cls.metaValue}>
                        {formatDate(s.transferDate)}
                      </span>
                    </div>
                    <div className={cls.metaItem}>
                      <span className={cls.metaLabel}>Submitted on</span>
                      <span className={cls.metaValue}>
                        {formatDateTime(s.createdAt)}
                      </span>
                    </div>
                    <div className={cls.metaItem}>
                      <span className={cls.metaLabel}>Last updated</span>
                      <span className={cls.metaValue}>
                        {formatDateTime(s.updatedAt)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className={cls.cardActions}>
                  <button
                    type="button"
                    className={cls.viewBtn}
                    onClick={() => handleViewDetails(s.id)}
                  >
                    <span>View details</span>
                    <span className={cls.viewIcon} aria-hidden="true">
                      →
                    </span>
                  </button>
                </div>
              </Motion.li>
            );
          })}
        </Motion.ul>
      )}
    </div>
  );
};

export default UserMemberPaymentSubmissions;
